import React, { useState } from 'react';
import SEO from '../components/SEO';
import { Phone, Mail, MapPin, Send, CheckCircle, AlertCircle, Facebook, Instagram } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { companyInfo } from '../data/mock';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const socialLinks = {
  facebook: "https://www.facebook.com/profile.php?id=61577688976534",
  instagram: "https://www.instagram.com/supaenergyhub"
};

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    postcode: '',
    message: '',
    service: 'solar'
  });
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (!response.ok) {
        throw new Error('Failed to submit enquiry. Please try again.');
      }
      
      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const serviceOptions = [
    { value: 'solar', label: 'Solar Panels' },
    { value: 'battery', label: 'Battery Storage' },
    { value: 'solar-battery', label: 'Solar + Battery' },
    { value: 'heat-pump', label: 'Heat Pump Hot Water' },
    { value: 'hvac', label: 'Heating & Cooling' },
    { value: 'ev-charger', label: 'EV Charger' },
    { value: 'other', label: 'Other / Not Sure' }
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="Contact Supa Energy Hub — Free Consultation"
        description="Get in touch with Supa Energy Hub for an obligation-free consultation on solar, battery, heat pump or full electrification — Australia-wide."
      />
      <Header />
      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-16 hero-gradient">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <span className="inline-block text-[#B5D43B] font-semibold text-sm tracking-wider uppercase mb-4">
                Contact Us
              </span>
              <h1 className="heading-1 mb-6">
                Let's Talk About Your Energy Goals
              </h1>
              <p className="body-large">
                No pressure, no obligations. Just a friendly conversation about 
                how we can help you take control of your home energy.
              </p>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
              {/* Contact Info */}
              <div>
                <h2 className="heading-3 mb-6">Get in Touch</h2>
                <p className="text-gray-600 leading-relaxed mb-8">
                  Whether you're just starting to explore your options or ready to get 
                  a quote, we're here to help. Reach out however works best for you.
                </p>
                
                <div className="space-y-6 mb-10">
                  <a 
                    href={`tel:${companyInfo.phone}`}
                    className="flex items-center gap-4 p-4 bg-white rounded-xl border border-black/5 hover:border-[#B5D43B]/30 hover:shadow-md transition-all"
                  >
                    <div className="w-12 h-12 rounded-xl bg-[#B5D43B]/15 flex items-center justify-center">
                      <Phone className="w-5 h-5 text-[#3D5A47]" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Give us a call</p>
                      <p className="font-semibold text-[#3D5A47]">{companyInfo.phone}</p>
                    </div>
                  </a>
                  
                  <a 
                    href={`mailto:${companyInfo.email}`}
                    className="flex items-center gap-4 p-4 bg-white rounded-xl border border-black/5 hover:border-[#B5D43B]/30 hover:shadow-md transition-all"
                  >
                    <div className="w-12 h-12 rounded-xl bg-[#B5D43B]/15 flex items-center justify-center">
                      <Mail className="w-5 h-5 text-[#3D5A47]" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Send an email</p>
                      <p className="font-semibold text-[#3D5A47]">{companyInfo.email}</p>
                    </div>
                  </a>
                  
                  <div className="flex items-center gap-4 p-4 bg-white rounded-xl border border-black/5">
                    <div className="w-12 h-12 rounded-xl bg-[#B5D43B]/15 flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-[#3D5A47]" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Service Area</p>
                      <p className="font-semibold text-[#3D5A47]">{companyInfo.location}</p>
                    </div>
                  </div>
                </div>

                {/* Response Promise */}
                <div className="p-6 bg-[#B5D43B]/10 rounded-2xl border border-[#B5D43B]/20">
                  <h3 className="font-semibold text-[#3D5A47] mb-2">Our Response Promise</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    We aim to respond to all enquiries within 24 hours. When you 
                    contact us, you'll speak with a real person who understands 
                    energy — not a call centre.
                  </p>
                </div>

                {/* Social Media */}
                <div className="mt-8">
                  <h3 className="font-semibold text-[#3D5A47] mb-4">Connect With Us</h3>
                  <div className="flex items-center gap-3">
                    <a
                      href={socialLinks.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 px-4 py-3 bg-white rounded-xl border border-black/5 hover:border-[#B5D43B]/30 hover:shadow-md transition-all group"
                    >
                      <div className="w-10 h-10 rounded-xl bg-[#1877F2]/10 group-hover:bg-[#1877F2] flex items-center justify-center transition-colors">
                        <Facebook className="w-5 h-5 text-[#1877F2] group-hover:text-white" />
                      </div>
                      <span className="font-medium text-[#3D5A47]">Facebook</span>
                    </a>
                    <a
                      href={socialLinks.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 px-4 py-3 bg-white rounded-xl border border-black/5 hover:border-[#B5D43B]/30 hover:shadow-md transition-all group"
                    >
                      <div className="w-10 h-10 rounded-xl bg-[#E4405F]/10 group-hover:bg-gradient-to-br group-hover:from-[#833AB4] group-hover:via-[#E4405F] group-hover:to-[#FCAF45] flex items-center justify-center transition-colors">
                        <Instagram className="w-5 h-5 text-[#E4405F] group-hover:text-white" />
                      </div>
                      <span className="font-medium text-[#3D5A47]">Instagram</span>
                    </a>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-white rounded-2xl border border-black/5 p-6 md:p-8">
                {submitted ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 rounded-full bg-[#B5D43B]/20 flex items-center justify-center mx-auto mb-6">
                      <CheckCircle className="w-8 h-8 text-[#3D5A47]" />
                    </div>
                    <h3 className="heading-3 mb-3">Thank You!</h3>
                    <p className="text-gray-600 mb-6">
                      We've received your enquiry and will be in touch within 24 hours.
                    </p>
                    <Button 
                      onClick={() => {
                        setSubmitted(false);
                        setFormData({
                          name: '',
                          email: '',
                          phone: '',
                          postcode: '',
                          message: '',
                          service: 'solar'
                        });
                      }}
                      className="btn-secondary"
                    >
                      Send Another Enquiry
                    </Button>
                  </div>
                ) : (
                  <>
                    <h2 className="heading-3 mb-6">Request a Free Estimate</h2>
                    <form onSubmit={handleSubmit} className="space-y-5">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div className="space-y-2">
                          <Label htmlFor="name">Full Name *</Label>
                          <Input
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="John Smith"
                            required
                            className="h-12"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="phone">Phone Number *</Label>
                          <Input
                            id="phone"
                            name="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="0400 000 000"
                            required
                            className="h-12"
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div className="space-y-2">
                          <Label htmlFor="email">Email Address *</Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="john@example.com"
                            required
                            className="h-12"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="postcode">Postcode</Label>
                          <Input
                            id="postcode"
                            name="postcode"
                            value={formData.postcode}
                            onChange={handleChange}
                            placeholder="3000"
                            className="h-12"
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="service">What are you interested in? *</Label>
                        <select
                          id="service"
                          name="service"
                          value={formData.service}
                          onChange={handleChange}
                          required
                          className="w-full h-12 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#B5D43B]"
                        >
                          {serviceOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="message">Your Message (Optional)</Label>
                        <Textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          placeholder="Tell us about your energy goals, current setup, or any questions..."
                          rows={4}
                          className="resize-none"
                        />
                      </div>
                      
                      {error && (
                        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                          <AlertCircle className="w-4 h-4 flex-shrink-0" />
                          <span>{error}</span>
                        </div>
                      )}
                      
                      <Button 
                        type="submit" 
                        className="btn-primary w-full gap-2"
                        disabled={isLoading}
                        data-testid="contact-submit-btn"
                      >
                        {isLoading ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4" />
                            Send Enquiry
                          </>
                        )}
                      </Button>
                      
                      <p className="text-xs text-gray-500 text-center">
                        By submitting this form, you agree to our Privacy Policy. 
                        We'll never share your information with third parties.
                      </p>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ContactPage;
